import React, { useState, useEffect } from 'react';
import './BookingCalendar.css';
import Select from 'react-select';
import { useNavigate } from "react-router-dom";
import BookingConfirmation from './BookingConfirmation';
import { hasPrivilege } from '../utils/hasPrivilege';
import { useUser } from '../context/UserContext';
import CancelConfirmation from './CalendarCancel';
import Swal from "sweetalert2";
import { FiRefreshCw } from 'react-icons/fi';
import SwimmingConfirmation from './SwimmingConfirmation';


const BookingCalendar = () => {
  const [selectedSport, setSelectedSport] = useState('');
  const token = localStorage.getItem('auth-token');
  const { userData } = useUser();
  const [showSwimmingPanel, setShowSwimmingPanel] = useState(false);
  const userId = userData?.userId;
  const [selectedPlayerDetails, setSelectedPlayerDetails] = useState(null);
  const playerDetailsRef = React.useRef(null);
  const { selectedCenter, setSelectedCenter } = useUser();
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedSlots, setSelectedSlots] = useState(new Set());
  const [sports, setSports] = useState([]);
  const [slotsData, setSlotsData] = useState({});
  const [courtMap, setCourtMap] = useState({});
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const [bookingDetails, setBookingDetails] = useState(null);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [hoveredSlot, setHoveredSlot] = useState(null);
  const [guestCount, setGuestCount] = useState('');


  const fetchData = async () => {
    setLoading(true);
    try {
      const headers = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      };


      const today = new Date().toISOString().split('T')[0];
      const dateToFetch = selectedDate || today;
      const response = await fetch(
        `${process.env.REACT_APP_API_COURT_SLOTS}?academyId=${selectedCenter}&date=${dateToFetch}&sportId=${selectedSport || ''}`,
        {
          method: 'GET',
          headers: headers,
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      const data = await response.json();

      if (data.success) {
        const fetchedSports = data.data.academySports.map((academySport) => ({
          label: academySport.sport.name,
          value: academySport.sport.id,
        }));
        setSports(fetchedSports);
        setSlotsData(data.data.slotsData || {});
        setCourtMap(data.data.courtMap || {});
      } else {
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchData();
  }, [selectedDate, selectedSport, selectedCenter]);
  useEffect(() => {
    setSelectedSlots(new Set());
    setGuestCount('');
  }, [selectedSport]);

  const formatTime = (timeString) => {
    const time = new Date(`1970-01-01T${timeString}Z`);
    const hours = time.getUTCHours();
    const minutes = time.getUTCMinutes();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const formattedHours = hours % 12 || 12;
    return `${formattedHours}:${minutes.toString().padStart(2, '0')} ${ampm}`;
  };

  const toggleSlotSelection = (courtId, timeSlot) => {
    const slotKey = `${courtId}-${timeSlot}`;
    const updatedSlots = new Set(selectedSlots);
    if (selectedSlots.has(slotKey)) {
      updatedSlots.delete(slotKey);
    } else {
      updatedSlots.add(slotKey);
    }
    setSelectedSlots(updatedSlots);
  };

  const handleBookSelectedSlots = async () => {
    if (!selectedSport || !selectedDate) {
      alert('Please select a sport and date before booking.');
      return;
    }

    if (selectedSlots.size === 0) {
      alert('No slots selected!');
      return;
    }

    const parsedSportId = parseInt(selectedSport);
    if (parsedSportId === 11 && (guestCount === '' || isNaN(guestCount) || parseInt(guestCount) <= 0)) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: 'Please enter a valid guest count for Swimming before proceeding.',
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 3000,
      });
      return;
    }
    let totalAvailable = 0;
    selectedSlots.forEach(slotKey => {
      const [courtId, timeSlot] = slotKey.split(/-(.*)/s);
      const normalizedTimeSlot = timeSlot.trim();
      const slotInfo = slotsData[normalizedTimeSlot]?.[courtId];
      if (slotInfo && slotInfo.availability === 'AVAILABLE') {
        totalAvailable += (slotInfo.maxPlayersAllowed - slotInfo.bookedPlayers);
      }
    });
    if (parseInt(guestCount) > totalAvailable) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: `You cannot book ${guestCount} guests. Only ${totalAvailable} spots available in selected slots.`,
        toast: true,
        position: "top-end",
        showConfirmButton: false,
        timer: 3000,
      });
      return;
    }

    const bookingDetails = {
      sportId: parsedSportId,
      date: selectedDate,
      academyId: selectedCenter,
      courtTimingIds: Array.from(selectedSlots).map(slot => {
        const [courtId, timeSlot] = slot.split(/-(.*)/s);
        const normalizedTimeSlot = timeSlot.trim();
        const slotInfo = slotsData[normalizedTimeSlot]?.[courtId];
        return slotInfo ? slotInfo.courtTimingId : null;
      }).filter(id => id !== null),
      guestCount: parsedSportId === 11 && !isNaN(parseInt(guestCount))
        ? parseInt(guestCount)
        : 0,
    };


    setBookingDetails(bookingDetails);
    setShowConfirmationModal(true);

  };


  useEffect(() => {
    if (sports.length > 0 && !selectedSport) {
      setSelectedSport(sports[0].value);
    }


    if (!selectedDate) {
      const today = new Date().toISOString().split('T')[0];
      setSelectedDate(today);
    }
  }, [sports, selectedDate]);

  const handleDateChange = (e) => {
    setSelectedDate(e.target.value);
    setSelectedSlots(new Set());
    setHoveredSlot(null);
    setGuestCount('');
  };

  const closeModal = () => {
    setShowConfirmationModal(false);
  };
  const getMinutesFromMidnight = (timeRange) => {
    const [startTime] = timeRange.split(' - ');
    const [time, meridian] = startTime.trim().split(' ');
    let [hours, minutes] = time.split(':').map(Number);

    if (meridian === 'PM' && hours !== 12) hours += 12;
    if (meridian === 'AM' && hours === 12) hours = 0;

    return hours * 60 + minutes;
  };
  useEffect(() => {
  }, [hoveredSlot]);
  const handleBookingSuccess = () => {
    setShowConfirmationModal(false);
    setSelectedSlots(new Set());
    fetchData();
  };
  const [isCancelMode, setIsCancelMode] = useState(false);
  const [cancelSelectedSlots, setCancelSelectedSlots] = useState(new Set());
  const [showCancelPanel, setShowCancelPanel] = useState(false);

  const toggleCancelSelection = (courtId, timeSlot) => {
    const slotKey = `${courtId}-${timeSlot}`;
    const updatedSlots = new Set(cancelSelectedSlots);
    updatedSlots.has(slotKey) ? updatedSlots.delete(slotKey) : updatedSlots.add(slotKey);
    setCancelSelectedSlots(updatedSlots);
  };

  const handleCancelSuccess = () => {
    setCancelSelectedSlots(new Set());
    setIsCancelMode(false);
    fetchData();
  };
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (playerDetailsRef.current && !playerDetailsRef.current.contains(event.target)) {
        setSelectedPlayerDetails(null);
      }
    };
    if (selectedPlayerDetails) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [selectedPlayerDetails]);
  const [cancelLoading, setCancelLoading] = useState(false);

const canModify = hasPrivilege(userData, 'COURTS', 'BOOKING_CALENDAR_MODIFY');
const canCancelBulk = hasPrivilege(userData, 'COURTS', 'CANCEL_BULK_BOOKING');

const tableOffsetPx = (canModify && canCancelBulk) ? 300 : 230;
const tableContainerStyle = {
  overflowY: 'auto',
  maxHeight: `calc(100dvh - ${tableOffsetPx}px)`,
};


  const handleSingleBookingCancel = async () => {
    if (!selectedPlayerDetails) return;


    const result = await Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Yes, cancel it!'
    });

    
    if (result.isConfirmed) {
      setCancelLoading(true);
      try {

        const courtId = Object.keys(courtMap).find(
          key => courtMap[key] === selectedPlayerDetails.court
        );

        if (!courtId) throw new Error('Court not found');

        const timeSlot = selectedPlayerDetails.time;
        const slotInfo = slotsData[timeSlot]?.[courtId];

        if (!slotInfo || !slotInfo.masterId) throw new Error('Booking information not found');


        const apiUrl = `${process.env.REACT_APP_API_CANCEL_COURT_BOOKING}?academyId=${selectedCenter}&bookingIds=${slotInfo.masterId}&loginUserId=${userId}`;

        const response = await fetch(apiUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error('Failed to cancel booking');
        }

        const data = await response.json();
        if (data.success) {
          Swal.fire({
            icon: 'success',
            title: 'Cancelled!',
            text: 'The booking has been cancelled.',
            toast: true,
            position: 'top-end',
            showConfirmButton: false,
            timer: 3000,
          });
          setSelectedPlayerDetails(null);
          fetchData();
        } else {
          throw new Error(data.message || 'Failed to cancel booking');
        }
      } catch (error) {
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: error.message || 'Failed to cancel booking',
        });
      } finally {
        setCancelLoading(false);
      }
    }
  };
  const getSourceColor = (source) => {
    const src = (source || '').toUpperCase();

    if (src.includes('PLAYO')) return '#3095B2';
    if (src.includes('WALK_IN')) return '#F48C06';
    if (src.includes('PLAYING_MEMBERSHIP')) return '#5E548E';
    if (src.includes('TRIAL')) return  '#8B4513';
    return '#B80000';

  };

  return (
    <div className="booking-container">
      <div className="sales-filters-login" style={{ marginBottom: '10px' }}>
        <div className="floating-select" style={{ minWidth: '30%' }}>
          <Select
            id="sport"
            className={`sales-filter-court-booking ${selectedSport ? "has-value" : ""}`}
            classNamePrefix="react-select"
            options={sports}
            value={sports.find(sport => sport.value === selectedSport)}
            onChange={(selectedOption) => setSelectedSport(selectedOption ? selectedOption.value : '')}
            placeholder="Select Sport"

            styles={{
              placeholder: (provided) => ({
                ...provided,
                color: 'black',
              }),
              menu: (provided) => ({
                ...provided,
                zIndex: 9999,
              }),
              menuPortal: (provided) => ({
                ...provided,
                zIndex: 9999,
              }),
            }}
            menuPortalTarget={document.body}

          />
          {selectedSport && <label htmlFor="sport">Sport</label>}
        </div>




        <div className="floating-select" style={{ minWidth: '30%' }}>
          <input
            type="date"
            id="date"
            className={`sales-filter-academy ${selectedDate ? "has-value" : ""}`}
            value={selectedDate}
            onChange={handleDateChange}
            onFocus={(e) => e.target.showPicker()}
          />
          {selectedDate && <label htmlFor="date">Select Date</label>}
        </div>

        <div className="booking-control">
          <button
            className="search-button"
            onClick={() => fetchData()}
            style={{
              padding: '8px',
              height: '38px',
              marginTop: '20px',
              backgroundColor: '#003b72',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer',
              width: '38px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <FiRefreshCw size={18} />
          </button>
        </div>
      </div>
      {Number(selectedSport) !== 11 && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '1%' }}>
          <div>
            {!isCancelMode ? (
              hasPrivilege(userData, 'COURTS', 'BOOKING_CALENDAR_MODIFY') && (
                <button
                  className="booking-main-button"
                  onClick={handleBookSelectedSlots}
                  disabled={selectedSlots.size === 0}
                >
                  Book Selected Slots ({selectedSlots.size})
                </button>
              )
            ) : (
              <button
                className="proceed-cancel-button"
                onClick={() => setShowCancelPanel(true)}
                disabled={cancelSelectedSlots.size === 0}
              >
                Proceed to Cancel ({cancelSelectedSlots.size})
              </button>
            )}
          </div>

          {hasPrivilege(userData, 'COURTS', 'CANCEL_BULK_BOOKING') && (
            <div>
              <button
                className={`cancel-mode-button ${isCancelMode ? 'active' : ''}`}
                onClick={() => {
                  setIsCancelMode(!isCancelMode);
                  setCancelSelectedSlots(new Set());
                  setSelectedSlots(new Set());
                }}
              >
                {isCancelMode ? 'Exit Cancel Mode' : 'Cancel Bookings'}
              </button>
            </div>
          )}
        </div>
      )}
      {loading && (
        <div className="premium-loader">
          <div className="progress-track">
            <div className="progress-bar"></div>
          </div>
          <div className="status-text">
            <span>L</span>
            <span>O</span>
            <span>A</span>
            <span>D</span>
            <span>I</span>
            <span>N</span>
            <span>G</span>
          </div>
        </div>
      )}

<div className="table-container" style={tableContainerStyle}>

        <table className="booking-table" >
          <thead>
            <tr>
              <th>Time</th>
              {Object.keys(courtMap).map((courtId) => (
                <th key={courtId}>{courtMap[courtId]}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Object.keys(slotsData)
              .sort((a, b) => getMinutesFromMidnight(a) - getMinutesFromMidnight(b))
              .map((timeSlot) => (

                <tr key={timeSlot}>
                  <td>{timeSlot}</td>
                  {Object.keys(courtMap).map((courtId) => {
                    const slotInfo = slotsData[timeSlot]?.[courtId];
                    const isAvailable = slotInfo && slotInfo.availability === 'AVAILABLE';
                    const isSelected = selectedSlots.has(`${courtId}-${timeSlot}`);
                    const isCancelSelected = cancelSelectedSlots.has(`${courtId}-${timeSlot}`);

                    return (
                      <td
                        key={courtId}
                        style={{ position: 'relative' }}
                      >
                        <button
                          className={`bc-button 
    ${isSelected ? 'bc-selected' : ''}
    ${isCancelSelected ? 'cancel-selected' : ''}
    ${isCancelMode ? 'cancel-mode' : ''}
    ${!slotInfo || (slotInfo.availability !== 'AVAILABLE' && slotInfo.availability !== 'BOOKED') ? 'unavailable' : ''}
  `}
                          style={{
                            backgroundColor: slotInfo?.availability === 'BOOKED' ? getSourceColor(slotInfo.source) : undefined,
                            color: slotInfo?.availability === 'BOOKED' ? '#fff' : undefined,
                            fontWeight: slotInfo?.availability === 'BOOKED' ? 'bold' : undefined,
                          }}
                          aria-label={`${isCancelMode ? 'Cancel' : 'Book'} Court ${courtMap[courtId]} at ${timeSlot}`}
                          disabled={
                            slotInfo?.availability === 'AVAILABLE' &&
                            parseInt(selectedSport) !== 11 &&
                            !hasPrivilege(userData, 'COURTS', 'BOOKING_CALENDAR_MODIFY')
                          }
                          title={
                            slotInfo?.availability === 'AVAILABLE' &&
                              parseInt(selectedSport) !== 11 &&
                              !hasPrivilege(userData, 'COURTS', 'BOOKING_CALENDAR_MODIFY')
                              ? 'Not authorized to book'
                              : ''
                          }

                          onClick={() => {
                            if (isCancelMode) {
                              if (
                                slotInfo?.availability === 'BOOKED' &&
                                slotInfo?.source !== 'PLAYO'
                              ) {
                                toggleCancelSelection(courtId, timeSlot);
                              }
                            } else {
                              if (slotInfo?.availability === 'BOOKED') {
                                setSelectedPlayerDetails({
                                  userName: slotInfo.bookedByUserName || 'N/A',
                                  userMobile: slotInfo.bookedByUserMobile || 'N/A',
                                  time: timeSlot,
                                  court: courtMap[courtId] || courtId,
                                  source: slotInfo.source || 'N/A',
                                  amount: slotInfo.amount || 0
                                });
                              } else if (slotInfo?.availability === 'AVAILABLE') {
                                const isSwimming = parseInt(selectedSport) === 11;

                                if (isSwimming) {
                                  const guestAlreadyBooked = slotInfo.bookedPlayers > 0;
                                  const hasAccess = hasPrivilege(userData, 'COURTS', 'BOOKING_CALENDAR_MODIFY');
                                  if (!guestAlreadyBooked && !hasAccess) return;

                                  const swimmingBookingDetails = {
                                    sportId: 11,
                                    date: selectedDate,
                                    court: courtMap[courtId],
                                    time: timeSlot,
                                    courtTimingId: slotInfo.courtTimingId,
                                    maxPlayersAllowed: slotInfo.maxPlayersAllowed,
                                    bookedPlayers: slotInfo.bookedPlayers,
                                    academyId: selectedCenter
                                  };
                                  setBookingDetails(swimmingBookingDetails);
                                  setShowSwimmingPanel(true);
                                } else if (hasPrivilege(userData, 'COURTS', 'BOOKING_CALENDAR_MODIFY')) {
                                  toggleSlotSelection(courtId, timeSlot);
                                }
                              }
                            }
                          }}
                        >
                          {slotInfo?.availability === 'BOOKED'
                            ? <span className="booked-name" style={{ color: 'white', fontWeight: 'bold' }}>{slotInfo.bookedByUserName}</span>
                            : slotInfo?.availability === 'AVAILABLE'
                              ? (parseInt(selectedSport) === 11
                                ? `${slotInfo.maxPlayersAllowed - slotInfo.bookedPlayers} available`
                                : 'Book')
                              : 'Unavailable'}
                        </button>
                      </td>





                    );
                  })}
                </tr>
              ))}
          </tbody>
        </table>
      </div>


      {Number(selectedSport) !== 11 && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '1%' }}>
          <div>
            {!isCancelMode ? (
              hasPrivilege(userData, 'COURTS', 'BOOKING_CALENDAR_MODIFY') && (
                <button
                  className="booking-main-button"
                  onClick={handleBookSelectedSlots}
                  disabled={selectedSlots.size === 0}
                >
                  Book Selected Slots ({selectedSlots.size})
                </button>
              )
            ) : (
              <button
                className="proceed-cancel-button"
                onClick={() => setShowCancelPanel(true)}
                disabled={cancelSelectedSlots.size === 0}
              >
                Proceed to Cancel ({cancelSelectedSlots.size})
              </button>
            )}
          </div>

          {hasPrivilege(userData, 'COURTS', 'CANCEL_BULK_BOOKING') && (
            <div>
              <button
                className={`cancel-mode-button ${isCancelMode ? 'active' : ''}`}
                onClick={() => {
                  setIsCancelMode(!isCancelMode);
                  setCancelSelectedSlots(new Set());
                  setSelectedSlots(new Set());
                }}
              >
                {isCancelMode ? 'Exit Cancel Mode' : 'Cancel Bookings'}
              </button>
            </div>
          )}
        </div>
      )}


      {showConfirmationModal && (
        <div className="booking-calendar-modal-overlay" onClick={closeModal}>
          <div className="booking-calendar-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="booking-calendar-close-modal" onClick={closeModal}>×</button>
            <BookingConfirmation
              bookingDetails={bookingDetails}
              onBookingSuccess={handleBookingSuccess}
            />
          </div>
        </div>
      )}
      {selectedPlayerDetails && (
        <div className="playerDetails-overlay">
          <div className="playerDetails" ref={playerDetailsRef}>
            <div className="playerDetails-header">
              <h3>Player Details</h3>
              <button onClick={() => setSelectedPlayerDetails(null)}>×</button>
            </div>
            <div className="userDetails">
              <h3>User Info</h3>
              <div className="userInfo">
                <p><strong>Name:</strong> {selectedPlayerDetails.userName}</p>
                <p><strong>Mobile:</strong> {selectedPlayerDetails.userMobile}</p>
                <p><strong>Time Slot:</strong> {selectedPlayerDetails.time}</p>
                <p><strong>Court Number:</strong> {selectedPlayerDetails.court}</p>
                <p><strong>Source:</strong> {selectedPlayerDetails.source}</p>
                <p><strong>Amount Paid:</strong> ₹{selectedPlayerDetails.amount}</p>

              </div>
              {hasPrivilege(userData, 'COURTS', 'CANCEL_BOOKING') &&
                selectedPlayerDetails.source !== 'PLAYO' && (
                  <button
                    className="cancel-mode-button"
                    onClick={handleSingleBookingCancel}
                    disabled={cancelLoading}
                  >
                    {cancelLoading ? 'Cancelling...' : 'Cancel Booking'}
                  </button>
                )}

            </div>
          </div>
        </div>
      )}

      {showCancelPanel && (
        <div className="cancel-panel-overlay show">
          <CancelConfirmation
            cancelDetails={Array.from(cancelSelectedSlots).map(slotKey => {
              const [courtId, timeSlot] = slotKey.split(/-(.*)/s);
              const slotInfo = slotsData[timeSlot]?.[courtId];
              return {
                courtId,
                courtName: courtMap[courtId],
                timeSlot,
                userName: slotInfo?.bookedByUserName,
                userMobile: slotInfo?.bookedByUserMobile,
                courtTimingId: slotInfo?.courtTimingId,
                masterId: slotInfo?.masterId
              };
            })}
            onCancelSuccess={handleCancelSuccess}
            onClose={() => setShowCancelPanel(false)}
          />
        </div>
      )}
      {showSwimmingPanel && (
        <div className="booking-calendar-modal-overlay" onClick={() => setShowSwimmingPanel(false)}>
          <div className="booking-calendar-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="booking-calendar-close-modal" onClick={() => setShowSwimmingPanel(false)}>×</button>
            <SwimmingConfirmation
              bookingDetails={bookingDetails}
              onClose={() => setShowSwimmingPanel(false)}
              onConfirm={(finalDetails) => {
                setShowSwimmingPanel(false);
                setSelectedSlots(new Set());
                fetchData();
              }}
            />


          </div>
        </div>
      )}


    </div>
  );
};

export default BookingCalendar;
